import { autoinject, bindable, computedFrom } from 'aurelia-framework';
import { AccountListItemVM, AccountListService, IAccountListItemAM } from '../account-api';
import { AccountGroupService, IAccountClassAM, IAccountGroupAM } from '../account-group-api';
import { AccountTagService, IAccountTagAM } from '../account-tag-api';

@autoinject
export class AccountList {
	@bindable accountChartId: string;
	@bindable selectedAccount: IAccountListItemAM;

	accountClasses: IAccountClassAM[];
	accountTags: IAccountTagAM[];
	accountGroupFilter: IAccountGroupAM;
	accountTagFilter: IAccountTagAM;
	countOfGrouplessAccounts: number;

	private accountsCache: AccountListItemVM[];
	private filteredAccounts: AccountListItemVM[];

	@computedFrom('filteredAccounts')
	get accounts(): AccountListItemVM[] {
		return this.filteredAccounts;
	}

	constructor(
		private accountService: AccountListService,
		private accountGroupService: AccountGroupService,
		private accountTagService: AccountTagService) {
	}

	initialize() {
		this.accountService.loadAccountList(this.accountChartId)
			.then(items => {
				this.accountsCache = items.map(item => new AccountListItemVM({
					id: item.id,
					name: item.name,
					accountNumber: item.accountNumber,
					accountGroup: item.accountGroup,
					accountTags: item.accountTags,
					removable: item.removable,
					excludeFromCalculation: item.excludeFromCalculation
				}));
				this.filterVisibleAccounts();
				this.refreshGrouplessAccountsCount();
			});
	}

	refreshGrouplessAccountsCount() {
		this.countOfGrouplessAccounts = this.accountsCache ? this.accountsCache.filter(a => !a.accountGroup).length : 0;
	}

	filterVisibleAccounts() {
		if (!this.accountGroupFilter && !this.accountTagFilter) {
			this.filteredAccounts = this.accountsCache;
		}

		const filters: Array<[any, (account: AccountListItemVM) => boolean]> = [
			[
				this.accountGroupFilter,
				account => account.accountGroup && account.accountGroup.id === this.accountGroupFilter.id
			],
			[
				this.accountTagFilter,
				account => account.accountTags.some(tag => tag.id === this.accountTagFilter.id)
			]
		];

		const filterer = (account: AccountListItemVM) => filters.every(f => !f[0] || f[1](account));

		this.filteredAccounts = this.accountsCache.filter(filterer);
	}

	attached() {
		this.initialize();
		this.accountGroupService.getAccountGroups().then(accountClasses => {
			this.accountClasses = accountClasses;
		});
		this.accountTagService.getAccountTags().then(accountTags => {
			this.accountTags = accountTags;
		});
	}

	listRowClicked(item: AccountListItemVM) {
		this.selectedAccount = item;
		return true;
	}

	setAccountGroupFilter(group: IAccountGroupAM) {
		this.accountGroupFilter = group;
		this.filterVisibleAccounts();
	}

	setAccountTagFilter(tag: IAccountTagAM) {
		this.accountTagFilter = tag;
		this.filterVisibleAccounts();
	}

	onAccountNumberChange(account: AccountListItemVM, value: string) {
		const previousAccountNumber = account.accountNumber;
		account.accountNumber = value;
		this.accountService.updateAccount(account).then(response => {
			if (!response.ok) {
				account.accountNumberValid = false;
				account.accountNumberEditing = true;
			}
		});
	}

	onAccountNameChange(account: AccountListItemVM, value: string) {
		const previousAccountName = account.name;
		account.name = value;
		this.accountService.updateAccount(account).then(response => {
			if (!response.ok) {
				account.nameValid = false;
				account.nameEditing = true;
			}
		});
	}

	async removeAccount(account: AccountListItemVM) {
		const listRemove = (list: AccountListItemVM[]) => {
			const index = list.indexOf(account);
			if (index !== -1) {
				list.splice(index, 1);
			}
		};

		await this.accountService.removeAccount(account.id);
		listRemove(this.filteredAccounts);
		listRemove(this.accountsCache);
	}

	async onAccountExcludeFromCalculationChange(account: AccountListItemVM) {
		const { id, excludeFromCalculation } = account;
		await this.accountService.setExcludeFromCalculation(id, excludeFromCalculation);
	}

	accountGroupChanged() {
		this.refreshGrouplessAccountsCount();
		this.filterVisibleAccounts();
	}
}
